import * as THREE from 'three';
import { HOTSPOTS, UI_CONFIG, XR_CONFIG } from '../config.js';
import { Hotspot, disposeSharedHotspotResources } from './Hotspot.js';
import { Raycaster } from './Raycaster.js';

function unwrapController(controller) {
  return controller?.controller
    ?? controller?.targetRay
    ?? controller?.object3D
    ?? controller;
}

function isControllerConnected(controller) {
  if (typeof controller?.connected === 'boolean') {
    return controller.connected;
  }

  const state = unwrapController(controller)?.userData?.xrControllerState;
  return typeof state?.connected === 'boolean' ? state.connected : true;
}

export class HotspotManager {
  constructor({
    scene = null,
    camera = null,
    infoPanel = null,
    hotspots = HOTSPOTS,
    controllers = null,
    domElement = null,
    onSelect = null,
    rayFar = XR_CONFIG.rayLength ?? 12,
  } = {}) {
    this.scene = scene;
    this.camera = camera;
    this.infoPanel = null;
    this.onSelect = onSelect;

    this.group = new THREE.Group();
    this.group.name = 'hotspots';
    this.hotspots = [];
    this.hotspotsById = new Map();
    this._hotspotTargets = [];
    this._hoveredHotspot = null;

    this.raycaster = new Raycaster({ far: rayFar, recursive: false });
    this.controllers = null;
    this._controllerBindings = [];
    this._desktopElement = null;
    this._desktopCamera = null;

    this._onDesktopPointerMove = this._handleDesktopPointerMove.bind(this);
    this._onDesktopPointerLeave = this._handleDesktopPointerLeave.bind(this);
    this._onDesktopClick = this._handleDesktopClick.bind(this);

    const baseScale = UI_CONFIG.hotspotBaseScale ?? 1;
    const hoverScale = UI_CONFIG.hotspotHoverScale ?? 1.16;

    for (let index = 0; index < hotspots.length; index += 1) {
      const hotspot = new Hotspot(hotspots[index], { baseScale, hoverScale });
      this.hotspots.push(hotspot);
      this.hotspotsById.set(hotspot.hotspotId, hotspot);
      this._hotspotTargets.push(hotspot.hitTarget);
      this.group.add(hotspot);
    }

    scene?.add(this.group);
    this.setInfoPanel(infoPanel);

    if (controllers) {
      this.connectControllers(controllers);
    }
    if (domElement) {
      this.connectDesktop(domElement, camera);
    }
  }

  setInfoPanel(infoPanel) {
    this.infoPanel = infoPanel;
    if (infoPanel && !infoPanel.parent && this.scene) {
      this.scene.add(infoPanel);
    }
    return this;
  }

  getHotspot(id) {
    return this.hotspotsById.get(id) ?? null;
  }

  getInteractiveMeshes() {
    return this._hotspotTargets;
  }

  getIntersectionFromController(controller) {
    if (!this.raycaster.setFromController(unwrapController(controller))) {
      return null;
    }
    return this._firstIntersectionFromCurrentRay();
  }

  getIntersectionFromPointer(pointer, camera = this.camera) {
    if (!this.raycaster.setFromCamera(pointer, camera)) {
      return null;
    }
    return this._firstIntersectionFromCurrentRay();
  }

  _firstIntersectionFromCurrentRay() {
    if (this.infoPanel?.isOpen) {
      const panelIntersection = this.raycaster.first(
        this.infoPanel.getInteractiveMeshes(),
        false,
      );
      if (panelIntersection) {
        return panelIntersection;
      }
    }

    return this.raycaster.first(this._hotspotTargets, false);
  }

  update(camera = this.camera) {
    for (let index = 0; index < this.hotspots.length; index += 1) {
      this.hotspots[index].updateFacing(camera);
    }
    this.infoPanel?.update(camera);
  }

  updateFromXRControllers(controllers = this.controllers) {
    if (!controllers) {
      this.clearHover();
      return null;
    }

    // Panel controls have priority so a hotspot behind the panel cannot steal focus.
    if (this.infoPanel?.isOpen) {
      for (let index = 0; index < controllers.length; index += 1) {
        if (!isControllerConnected(controllers[index])) {
          continue;
        }
        if (!this.raycaster.setFromController(unwrapController(controllers[index]))) {
          continue;
        }

        const intersection = this.raycaster.first(
          this.infoPanel.getInteractiveMeshes(),
          false,
        );
        if (intersection) {
          this._setHoveredHotspot(null);
          this.infoPanel.setHoveredAction(
            this.infoPanel.getActionFromIntersection(intersection),
          );
          return intersection;
        }
      }
    }

    this.infoPanel?.setHoveredAction(null);

    for (let index = 0; index < controllers.length; index += 1) {
      if (!isControllerConnected(controllers[index])) {
        continue;
      }
      if (!this.raycaster.setFromController(unwrapController(controllers[index]))) {
        continue;
      }

      const intersection = this.raycaster.first(this._hotspotTargets, false);
      if (intersection) {
        this._setHoveredHotspot(this.getHotspotFromIntersection(intersection));
        return intersection;
      }
    }

    this._setHoveredHotspot(null);
    return null;
  }

  updateFromPointer(pointer, camera = this.camera) {
    const intersection = this.getIntersectionFromPointer(pointer, camera);

    if (intersection?.object?.userData?.interactionType === 'xr-ui') {
      this._setHoveredHotspot(null);
      this.infoPanel?.setHoveredAction(
        this.infoPanel.getActionFromIntersection(intersection),
      );
    } else {
      this.infoPanel?.setHoveredAction(null);
      this._setHoveredHotspot(this.getHotspotFromIntersection(intersection));
    }

    return intersection;
  }

  selectFromController(controller, camera = this.camera) {
    const intersection = this.getIntersectionFromController(controller);
    return this.selectIntersection(intersection, camera);
  }

  selectFromPointer(pointer, camera = this.camera) {
    const intersection = this.getIntersectionFromPointer(pointer, camera);
    return this.selectIntersection(intersection, camera);
  }

  selectIntersection(intersection, camera = this.camera) {
    if (!intersection) {
      return null;
    }

    if (intersection.object.userData.interactionType === 'xr-ui') {
      const action = this.infoPanel?.getActionFromIntersection(intersection);
      if (action && this.infoPanel.handleAction(action)) {
        return { type: 'ui', action };
      }
      return null;
    }

    const hotspot = this.getHotspotFromIntersection(intersection);
    if (!hotspot) {
      return null;
    }

    this.selectHotspot(hotspot, camera);
    return { type: 'hotspot', hotspot };
  }

  selectHotspot(hotspotOrId, camera = this.camera) {
    const hotspot = typeof hotspotOrId === 'string'
      ? this.getHotspot(hotspotOrId)
      : hotspotOrId;

    if (!hotspot) {
      return false;
    }

    this.infoPanel?.open(hotspot, camera);
    this.onSelect?.(hotspot.data, hotspot, this);
    return true;
  }

  getHotspotFromIntersection(intersection) {
    let object = intersection?.object ?? null;
    while (object) {
      if (object.userData?.hotspot) {
        return object.userData.hotspot;
      }
      object = object.parent;
    }
    return null;
  }

  clearHover() {
    this._setHoveredHotspot(null);
    this.infoPanel?.setHoveredAction(null);
  }

  _setHoveredHotspot(hotspot) {
    if (this._hoveredHotspot === hotspot) {
      return;
    }

    this._hoveredHotspot?.setHovered(false);
    this._hoveredHotspot = hotspot;
    this._hoveredHotspot?.setHovered(true);
  }

  connectControllers(controllers) {
    this.disconnectControllers();
    this.controllers = controllers;

    for (let index = 0; index < controllers.length; index += 1) {
      const controller = unwrapController(controllers[index]);
      if (!controller?.addEventListener) {
        continue;
      }

      const handler = () => this.selectFromController(controller, this.camera);
      controller.addEventListener('select', handler);
      this._controllerBindings.push({ controller, handler });
    }

    return this;
  }

  disconnectControllers() {
    for (let index = 0; index < this._controllerBindings.length; index += 1) {
      const binding = this._controllerBindings[index];
      binding.controller.removeEventListener('select', binding.handler);
    }
    this._controllerBindings.length = 0;
    this.controllers = null;
  }

  connectDesktop(domElement, camera = this.camera) {
    this.disconnectDesktop();
    this._desktopElement = domElement;
    this._desktopCamera = camera;
    domElement.addEventListener('pointermove', this._onDesktopPointerMove);
    domElement.addEventListener('pointerleave', this._onDesktopPointerLeave);
    domElement.addEventListener('click', this._onDesktopClick);
    return this;
  }

  disconnectDesktop() {
    if (!this._desktopElement) {
      return;
    }
    this._desktopElement.removeEventListener('pointermove', this._onDesktopPointerMove);
    this._desktopElement.removeEventListener('pointerleave', this._onDesktopPointerLeave);
    this._desktopElement.removeEventListener('click', this._onDesktopClick);
    this._desktopElement = null;
    this._desktopCamera = null;
  }

  _handleDesktopPointerMove(event) {
    const pointer = this.raycaster.setPointerFromEvent(event, this._desktopElement);
    this.updateFromPointer(pointer, this._desktopCamera);
  }

  _handleDesktopPointerLeave() {
    this.clearHover();
  }

  _handleDesktopClick(event) {
    const pointer = this.raycaster.setPointerFromEvent(event, this._desktopElement);
    this.selectFromPointer(pointer, this._desktopCamera);
  }

  dispose({ disposeSharedResources = false } = {}) {
    this.disconnectControllers();
    this.disconnectDesktop();
    this.clearHover();

    for (let index = 0; index < this.hotspots.length; index += 1) {
      this.hotspots[index].dispose();
    }

    this.hotspots.length = 0;
    this._hotspotTargets.length = 0;
    this.hotspotsById.clear();
    this.group.removeFromParent();

    if (disposeSharedResources) {
      disposeSharedHotspotResources();
    }
  }
}

export default HotspotManager;
